import React from "react";
import PropTypes from "prop-types";
import { usePageContext } from "../../context/PageContext";
import { navigate } from "gatsby";
import { MerchPromos } from "@sunwing/wvi-components";
import { addSEOSupportToURL } from "../../utils/gatsby-helpers";
import { cloudinaryGenURL } from "../../utils/cloudinaryHelpers";
import { RCL as useTranslation } from "../../components/RCL";
import { NoResultsCards } from "../../components/NoResultsCards/NoResultsCards";
import * as styles from "./PromotionRule.module.scss";

const buildImageData = offer => {
	const accommodationName = offer?.accommodationInfo?.accommodationName || "Hotel";
	const destinationName = offer?.destination?.name;

	if (offer.images) {
		return {
			source:
				cloudinaryGenURL({
					cloudinaryImage: offer.images[0],
				})?.data || {},
			alt: `${accommodationName} - ${destinationName}`,
		};
	}

	return null;
};

const transformMerchandisingData = merchandising => {
	if (!merchandising) return [];

	return merchandising
		.filter(i => i)
		.map(gatewayObj => ({
			Gateway: {
				Name: gatewayObj.gateway?.name,
				Code: gatewayObj.gateway?.code,
			},
			PromotionGroups: (gatewayObj.promotionGroups || []).map(group => ({
				title: group.title,
				ProductCategory: group.productCategory,
				Offers: (group.offers || []).map(offer => ({
					Gateway: {
						Name: offer.gateway?.name,
						Code: offer.gateway?.code,
					},
					Destination: {
						Name: offer.destination?.name,
						ProviderCode: offer.destination?.providerCode,
						CountryName: offer.destination?.countryName,
					},
					Collection: (offer.collection || [])
						.map(col =>
							col.collectionName
								? {
										collectionName: {
											NameOfTheConceptOrCollection:
												col.collectionName?.nameOfTheConceptOrCollection,
										},
									}
								: null
						)
						.filter(Boolean),
					MealPlan: offer.mealPlan,
					DeepLink: offer.deepLink,
					CalendarLink: offer.calendarLink,
					AccommodationInfo: {
						AccommodationCode: offer.accommodationInfo?.accommodationCode,
						AccommodationName: offer.accommodationInfo?.accommodationName,
						StarRating: offer.accommodationInfo?.starRating,
					},
					RegPrice: offer.regPrice,
					SaveUpto: offer.saveUpto,
					tax: offer.tax,
					OfferType: offer.offerType,
					DepartureDate: offer.departureDate,
					Duration: offer.duration,
					Price: offer.price,
					image: buildImageData(offer),
				})),
			})),
		}));
	// .map(merchant => ({
	// 	...merchant,
	// 	promotionGroups:
	// 		merchant.promotionGroups?.map(group => ({
	// 			...group,
	// 			offers:
	// 				group.Offers?.map(offer => ({
	// 					...offer,
	// 					image: buildImageData(offer),
	// 				})) || [],
	// 		})) || [],
	// }));
};

const PromotionRuleConnector = ({ data }) => {
	const contextValue = usePageContext();

	const { locale, basePath, preferredGateway, setPreferredGateway } = contextValue;

	const labels = {
		from: useTranslation({ searchKey: "from" }),
		loadAll: useTranslation({ searchKey: "load-all" }),
		loadMore: useTranslation({ searchKey: "load-more" }),
		adModuleDisclaimer: useTranslation({ searchKey: "ad-module-disclaimer" }),
		days: useTranslation({ searchKey: "days" }),
		saveUpTo: useTranslation({ searchKey: "save-up-to" }),
		oneWay: useTranslation({ searchKey: "one-way" }),
		roundTrip: useTranslation({ searchKey: "round-trip" }),
		// Merchandising Card (Packages)
		adultsOnly: useTranslation({ searchKey: "adults-only" }),
		departure: useTranslation({ searchKey: "departure" }),
		return: useTranslation({ searchKey: "vwq-return" }),
		perAdult: useTranslation({ searchKey: "per-adult" }),
		taxAndFees: useTranslation({ searchKey: "vwq-taxes-and-fees-incl" }),
		was: useTranslation({ searchKey: "vwq-was" }),
		apply: useTranslation({ searchKey: "apply" }),
		reset: useTranslation({ searchKey: "reset" }),
		// Filters
		sortAndFilter: useTranslation({ searchKey: "sort-and-filter" }),
		destinations: useTranslation({ searchKey: "destinations" }),
		clearAll: useTranslation({ searchKey: "clear-all" }),
		priceRange: useTranslation({ searchKey: "price-range" }),
		pricePerPerson: useTranslation({ searchKey: "price-per-person-cad" }),
		starRating: useTranslation({ searchKey: "filter-star-rating" }),
		sortBy: useTranslation({ searchKey: "sort-by" }),
		recommended: useTranslation({ searchKey: "sort-by-recommended" }),
		priceLowToHigh: useTranslation({ searchKey: "price-low-to-high" }),
		priceHighToLow: useTranslation({ searchKey: "price-high-to-low" }),
		starRatingLowToHigh: useTranslation({ searchKey: "star-rating-low-to-high" }),
		starRatingHighToLow: useTranslation({ searchKey: "star-rating-high-to-low" }),
		hotelNameAToZ: useTranslation({ searchKey: "hotel-name-a-to-z" }),
		hotelNameZToA: useTranslation({ searchKey: "hotel-name-z-to-a" }),
		departureDateEarliest: useTranslation({ searchKey: "departure-date-earliest-available" }),
	};

	const transformedData = transformMerchandisingData(data?.merchandising);

	const defaultGateway =
		preferredGateway?.label && preferredGateway?.value
			? {
					Code: preferredGateway.value,
					Name: preferredGateway.label,
				}
			: undefined;

	return (
		<div data-promotion-deal className={styles.merchPromo}>
			<MerchPromos
				labels={labels}
				data={transformedData}
				locale={locale}
				numberToShow={data?.numberToShow}
				moreToShow={data?.moreToShow}
				defaultGateway={defaultGateway}
				onGatewayChange={(gatewayCode, gatewayName) => {
					// Update preferred gateway in context
					if (setPreferredGateway && typeof setPreferredGateway === "function") {
						setPreferredGateway({
							label: gatewayName,
							value: gatewayCode,
						});
					}
					// Homepage check
					const pathSegments = basePath.split("/").filter(segment => segment.length > 0);
					const isHomepage = pathSegments.length <= 1;

					// Navigate if NOT homepage
					if (!isHomepage) {
						const pathPrefix = locale === "fr-CA" ? "de" : "from";
						navigate(addSEOSupportToURL(`${basePath}/${pathPrefix}-${gatewayName}`));
						return true;
					}

					// Homepage: let component handle state internally
					return false;
				}}
				noResults={<NoResultsCards locale={locale} />}
			/>
		</div>
	);
};

PromotionRuleConnector.propTypes = {
	data: PropTypes.shape({
		numberToShow: PropTypes.number,
		moreToShow: PropTypes.number,
		merchandising: PropTypes.arrayOf(
			PropTypes.shape({
				gateway: PropTypes.shape({
					code: PropTypes.string,
					name: PropTypes.string,
				}),
				promotionGroups: PropTypes.arrayOf(
					PropTypes.shape({
						description: PropTypes.string,
						offers: PropTypes.arrayOf(
							PropTypes.shape({
								accommodationInfo: PropTypes.shape({
									accommodationCode: PropTypes.string,
									accommodationName: PropTypes.string,
									starRating: PropTypes.number,
								}),
								calendarLink: PropTypes.string,
								deepLink: PropTypes.string,
								departureDate: PropTypes.string,
								destination: PropTypes.shape({
									countryName: PropTypes.string,
									name: PropTypes.string,
									providerCode: PropTypes.string,
								}),
								duration: PropTypes.number,
								gateway: PropTypes.shape({
									code: PropTypes.string,
									name: PropTypes.string,
								}),
								mealPlan: PropTypes.string,
								offerType: PropTypes.string,
								price: PropTypes.number,
								regPrice: PropTypes.number,
								saveUpto: PropTypes.number,
								tax: PropTypes.string,
							})
						),
						title: PropTypes.string,
					})
				),
			})
		),
	}),
	useMockData: PropTypes.bool,
};

PromotionRuleConnector.defaultProps = {
	data: null,
	useMockData: false,
};

export default PromotionRuleConnector;
export { PromotionRuleConnector };
